import React, { useRef, useState, useEffect } from 'react';
import styles from './style.module.scss';
import Image from 'next/image';
import { useIsMobile } from 'hooks/use-size-class';
import { buildClassName } from 'utils/build-class-name';
import type { CommercialBenefits } from '@tenants';

export type BenefitsProps = {
  cards: CommercialBenefits['benefits'];
  joinUsLink: CommercialBenefits['joinUsLink'];
};


const generateRandomWidths = (count: number) => {
  const minWidth = 500;
  const maxWidth = 600;
  const minDifference = 40; // Minimum difference between consecutive widths
  const widths: number[] = [];

  for (let i = 0; i < count; i++) {
    let newWidth: number;
    let attempts = 0;
    const maxAttempts = 10;

    do {
      newWidth = Math.floor(Math.random() * (maxWidth - minWidth + 1)) + minWidth;
      attempts++;
    } while (
      attempts < maxAttempts &&
      widths.length > 0 &&
      Math.abs(newWidth - widths[widths.length - 1]) < minDifference
    );

    widths.push(newWidth);
  }

  return widths;
};

export default function Benefits({ cards, joinUsLink }: BenefitsProps) {
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const wrapperContainerRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);
  const [visibleCardIndexes, setVisibleCardIndexes] = useState<number[]>([]);
  const [sectionHeight, setSectionHeight] = useState(0);
  const cardRefs = useRef<(HTMLDivElement | null)[]>(new Array(cards.length).fill(null));
  const isMobile = useIsMobile();
  const [randomWidths, setRandomWidths] = useState<number[]>([]);
  const imageCards = cards.filter(card => card.type !== 'card').length
  
  useEffect(() => {
    setRandomWidths(generateRandomWidths(imageCards));
  }, [imageCards]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setSectionHeight(window.innerHeight);
    }
  }, []);

  useEffect(() => {
    if (!cardRefs.current.every(el => !!el)) return;
    const lastVisibleIndex = cardRefs.current.findLastIndex((cardRef, _idx) => {
      if (!cardRef) return false;
      const rect = cardRef.getBoundingClientRect();
      return rect.left < window.innerWidth && rect.right > 0;
    });
    const newVisibleIndexes = Array.from({ length: lastVisibleIndex + 1 }, (_, index) => index);

    // Only update state if visibility changed
    if (newVisibleIndexes.length !== visibleCardIndexes.length) {
      setVisibleCardIndexes(newVisibleIndexes);
    }
  }, [translateX, visibleCardIndexes.length, cards.length]);

  useEffect(() => {
    if (isMobile) return;
    const handleScroll = () => {
      const allRefsAttached = cardRefs.current.every(el => !!el);
      if (!cardsContainerRef.current || !wrapperContainerRef.current || !allRefsAttached) return;
      const container = cardsContainerRef.current;
      const containerWidth = container.offsetWidth;
      const scrollWidth = container.scrollWidth - containerWidth;

      const wrapperContainer = wrapperContainerRef.current;
      const wrapperContainerHeight = wrapperContainer.offsetHeight - window.innerHeight;

      // Calculate how far through the section we've scrolled (0 to 1)
      const wrapperContainerTop = wrapperContainer.getBoundingClientRect().top;
      const wrapperContainerScrolled = -wrapperContainerTop;
      const scrollProgress = Math.max(0, Math.min(1, wrapperContainerScrolled / wrapperContainerHeight));

      // Set the horizontal scroll position based on vertical scroll progress
      const newTranslateX = -scrollWidth * scrollProgress;
      setTranslateX(newTranslateX);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, wrapperContainerRef, cardsContainerRef, cards.length]);

  // Calculate the optimal section height once the component has mounted and refs are available
  useEffect(() => {
    if (!isMobile && cardsContainerRef.current) {
      const container = cardsContainerRef.current;
      const containerWidth = container.offsetWidth;
      const cardsTrackWidth = container.scrollWidth;

      // Calculate how much horizontal space needs to be scrolled
      const scrollableWidth = cardsTrackWidth - containerWidth;

      // Determine the viewport height
      const viewportHeight = window.innerHeight;

      // Calculate a factor to determine how many viewport heights we need
      // to achieve a smooth scroll through all cards
      // You can adjust this multiplier to control the scroll speed
      const scrollFactor = 3; // This is adjustable - higher values = slower scroll

      // Calculate the height needed
      // We add one viewport height to ensure the section starts and ends with full visibility
      const calculatedHeight = ((scrollableWidth / containerWidth) * viewportHeight * scrollFactor + viewportHeight);

      // technically this should be minus whatever the navbar height is
      // but we can live with it
      setSectionHeight(calculatedHeight);
    }
  }, [isMobile]);

  const setRefs = (el: HTMLDivElement, idx: number) => {
    cardRefs.current[idx] = el;
    cardRefs.current = [...cardRefs.current];
  };

  let cardCount = 0;
  let cardMediaCount = 0;
  return (
    <div
      ref={wrapperContainerRef}
      className={styles.section}
      style={{ height: !isMobile ? `${sectionHeight}px` : undefined }}
    >
      <div className={styles.stickyContainer}>
        <div className={styles.logo}>
          <Image
            src="/next/assets/images/exp-commercial/eXp_Commercial_single_line.svg"
            alt="eXp Commercial Logo"
            fill
            style={{ objectFit: 'contain' }}
          />
        </div>
        <div
          ref={cardsContainerRef}
          className={styles.horizontalScroll}
        >
          <div
            className={styles.cardsTrack}
            style={{
              transform: `translateX(${translateX}px)`,
            }}
          >
            {cards.map((card, index) => {
              if (card.type === 'card') {
                cardCount++;
                const isEven = cardCount % 2 === 0;
                return (
                  <div
                    // TODO: remove index suffix once we have unique card titles
                    key={`${card.title}-${index}`}
                    ref={el => setRefs(el, index)}
                    className={buildClassName(styles.card, !visibleCardIndexes.includes(index) && !isMobile ? styles.hidden : '', isEven ? styles.even : '')}
                  >
                    <div className={styles.cardHeader}>
                      <div className={styles.cardNumber}>{cardCount}</div>
                      {card.titleHtml && (
                        <h3 className={styles.cardTitle} dangerouslySetInnerHTML={{ __html: card.titleHtml }} />
                      )}
                      {!card.titleHtml && (
                        <h3 className={styles.cardTitle}>{card.title}</h3>
                      )}
                    </div>
                    <p className={styles.cardDescription}>{card.description}</p>
                  </div>
                );
              }
              cardMediaCount++;
              return (
                <div
                  key={card.url}
                  ref={el => setRefs(el, index)}
                  className={buildClassName(styles.cardMedia, !visibleCardIndexes.includes(index) && !isMobile ? styles.hidden : '')}
                  style={{ width: `${randomWidths[cardMediaCount - 1]}px` }}
                >
                  <Image src={card.url} alt={card.altText} fill loading="lazy" style={{ objectFit: 'cover' }} sizes='40vw' />
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles.footer}>
          <a href={joinUsLink} className={styles.footerLink}>
            <h2>
              Join Us
            </h2>
            <div className={styles.arrow}>
              <Image src="/assets/images/right-direction-arrow.svg" fill alt="arrow" />
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}